/**
 * SMS messaging panel state, lifted to `StaffLayout` so the top-center
 * pill and a tenant profile's "Text" button drive the exact same panel
 * instance regardless of which one opened it — clicking "Text" on a
 * tenant while the panel is closed opens it pre-scoped to that tenant;
 * clicking the header pill with nothing else open defaults to the inbox.
 *
 * `openConversationForTenant` is the messaging module's one choke point
 * for "start texting this tenant" (confirmed: the header pill only ever
 * calls `openInbox`), which is what makes it the right place to gate on
 * SMS consent rather than duplicating the check at every call site. It
 * checks consent before ever reaching `{view:'tenant'}`:
 *
 *   CONSENTED  -> straight to {view:'tenant'}, unchanged from before this
 *                 gate existed.
 *   DECLINED   -> {view:'consent-blocked', reason:'declined'}, no dialog —
 *                 texting a tenant who said no is not offered as an option.
 *   NOT_ASKED  -> {view:'consent-required'}, which renders
 *                 SmsConsentDialog mode="gate" in StaffLayout. Its Yes path
 *                 transitions on to {view:'tenant'}; its No path records
 *                 the decline and closes.
 *   fetch error -> fails closed, same posture MessagingPill.tsx already
 *                  takes when /messaging/status fails.
 *
 * `resolveConversationTarget` (modules/messaging/service.ts) enforces the
 * same rule server-side — this is a UX flow, not the actual security
 * boundary.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { fetchSmsConsent } from './sms-consent.js';

export interface MessagingTenant {
  id: string;
  name: string;
}

export type MessagingPanelState =
  | { view: 'closed' }
  | { view: 'inbox' }
  | { view: 'tenant'; tenant: MessagingTenant }
  | { view: 'consent-check'; tenant: MessagingTenant }
  | { view: 'consent-required'; tenant: MessagingTenant }
  | { view: 'consent-blocked'; tenant: MessagingTenant; reason: 'declined' | 'error' };

interface MessagingContextValue {
  state: MessagingPanelState;
  openInbox: () => void;
  openConversationForTenant: (tenant: MessagingTenant) => Promise<void>;
  /** Called by SmsConsentDialog's Yes path (mode="gate") once consent is
   *  recorded — advances straight to the conversation panel without a
   *  second round trip, since setSmsConsent already returned CONSENTED. */
  proceedToConversation: (tenant: MessagingTenant) => void;
  close: () => void;
}

const MessagingContext = createContext<MessagingContextValue | null>(null);

export function MessagingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MessagingPanelState>({ view: 'closed' });

  const value = useMemo<MessagingContextValue>(
    () => ({
      state,
      openInbox: () => setState({ view: 'inbox' }),
      openConversationForTenant: async (tenant) => {
        setState({ view: 'consent-check', tenant });
        try {
          const consent = await fetchSmsConsent(tenant.id);
          if (consent.status === 'CONSENTED') {
            setState({ view: 'tenant', tenant });
          } else if (consent.status === 'DECLINED') {
            setState({ view: 'consent-blocked', tenant, reason: 'declined' });
          } else {
            setState({ view: 'consent-required', tenant });
          }
        } catch {
          setState({ view: 'consent-blocked', tenant, reason: 'error' });
        }
      },
      proceedToConversation: (tenant) => setState({ view: 'tenant', tenant }),
      close: () => setState({ view: 'closed' }),
    }),
    [state],
  );

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
}

export function useMessaging(): MessagingContextValue {
  const context = useContext(MessagingContext);
  if (!context) throw new Error('useMessaging must be used within a MessagingProvider.');
  return context;
}
